import {
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import React, { Component } from "react";
import { languages } from "../../constants/languages";
import TournamentTypes from "../../constants/TournamentTypes";

class TournamentExportContainer extends Component {
  state = {
    loading: false,
    totalTournaments: 0,
    exportedCount: 0,
    error: null,
    progress: 0,
    logs: [],
    csvData: null,
    start: moment(),
    end: moment().add(1, "months"),
  };

  componentDidMount() {
    // Data will be loaded when user clicks submit button
  }

  addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    console.log(logEntry);
    this.setState((prevState) => ({
      logs: [...prevState.logs, logEntry],
    }));
  };

  handleStartChange = (date) => {
    this.setState({
      start: date,
    });
  };

  handleEndChange = (date) => {
    this.setState({
      end: date,
    });
  };

  loadTournamentData = async () => {
    try {
      this.setState({ loading: true, error: null, logs: [], progress: 0 });
      this.addLog("Starting tournament data export...");

      const startDate = this.state.start.startOf("day").unix();
      const endDate = this.state.end.endOf("day").unix();

      this.addLog(`Date range: from ${startDate} to ${endDate}`);

      this.addLog("Querying tournaments with status 1, 2, or 3");
      const startTime = Date.now();

      const tournamentsQuery = await window.firebase
        .firestore()
        .collection("tournaments")
        .where("status", "in", [1, 2, 3])
        .where("date", ">=", startDate)
        .where("date", "<=", endDate)
        .get();

      const queryTime = Date.now() - startTime;
      this.addLog(
        `Found ${tournamentsQuery.size} tournaments in ${queryTime}ms`,
      );

      const tournaments = [];
      let processedCount = 0;

      tournamentsQuery.forEach((doc) => {
        const tournament = { id: doc.id, ...doc.data() };

        // Filter out tournaments that don't have numTeams defined
        if (tournament.numTeams !== undefined && tournament.numTeams !== null) {
          tournaments.push(tournament);
        }

        processedCount++;

        if (processedCount % 100 === 0) {
          this.setState({
            progress: (processedCount / tournamentsQuery.size) * 100,
            exportedCount: processedCount,
          });
        }
      });

      this.addLog(
        `Processing ${tournaments.length} tournaments (filtered from ${tournamentsQuery.size} total)...`,
      );

      // Create CSV data
      const csvData = this.createCSVData(tournaments);

      const totalTime = Date.now() - startTime;
      this.addLog(`Export complete in ${totalTime}ms`);
      this.addLog(`Generated CSV with ${tournaments.length} tournaments`);

      this.setState({
        loading: false,
        totalTournaments: tournaments.length,
        exportedCount: tournaments.length,
        progress: 100,
        csvData: csvData,
      });
    } catch (error) {
      console.error("Error loading tournament data:", error);
      this.addLog(`Error: ${error.message}`);
      this.setState({
        loading: false,
        error: "Failed to load tournament data",
      });
    }
  };

  createCSVData = (tournaments) => {
    // Helper function to get sport name
    const getSportName = (sportId) => {
      const sport = TournamentTypes.find((type) => type.id === sportId);
      return sport ? sport.name : "";
    };

    // Helper function to get status name
    const getStatusName = (status) => {
      const statusMap = {
        1: "Worldclass",
        2: "Legendary",
        3: "Pro",
      };
      return statusMap[status] || status;
    };

    // Helper function to get level name
    const getLevelName = (level) => {
      if (level === null || level === undefined) {
        return "";
      }
      const levelMap = {
        1: "Basic",
        2: "Experienced",
        3: "Advanced",
      };
      return levelMap[level] || level;
    };

    // Define CSV headers
    const headers = [
      "id",
      "date",
      "name",
      "status",
      "uid",
      "sport",
      "individualPlayers",
      "color",
      "liveLink",
      "app_active",
      "country",
      "age_min",
      "age_max",
      "level",
      "gender_men",
      "gender_women",
      "numDivisions",
      "numLocations",
      "numMatchDays",
      "numPlayers",
      "teamsAsRefs",
      "numReferees",
      "numTeams",
      "numMatches",
      "numBreaks",
      "numAdmins",
      "numLanguages",
      "numFields",
      "numCustomPoints",
      "numProducts",
      "numQuestions",
      "numSponsors",
      "matchDuration",
      "customMatchDuration",
      "timeBetweenMatches",
      "description",
      "copy",
      "useSets",
      "advancedGroupRanking",
      "onlinePayment",
      "manualPayment",
      "currency",
      "online",
      "requireCheckIn",
      "pricePerDivision",
      "averageSignupPrice",
    ];

    // Helper function to check if customMatchDuration has any non-empty values
    const hasCustomMatchDuration = (tournament) => {
      if (
        !tournament.customMatchDuration ||
        typeof tournament.customMatchDuration !== "object"
      ) {
        return false;
      }
      const values = Object.values(tournament.customMatchDuration);
      return values.some(
        (value) => value !== "" && value !== null && value !== undefined,
      );
    };

    // Helper function to calculate average signup price
    const calculateAverageSignupPrice = (tournament) => {
      // Check if pricePerDivision is true and divisionSignupPrice exists
      if (
        tournament.pricePerDivision === true &&
        tournament.divisionSignupPrice &&
        typeof tournament.divisionSignupPrice === "object"
      ) {
        const prices = Object.values(tournament.divisionSignupPrice);
        if (prices.length > 0) {
          const numericPrices = prices
            .map((price) => parseFloat(price))
            .filter((price) => !isNaN(price));

          if (numericPrices.length > 0) {
            const average =
              numericPrices.reduce((sum, price) => sum + price, 0) /
              numericPrices.length;
            return average.toFixed(2);
          }
        }
      }

      // Fallback to simple signupPrice
      if (tournament.signupPrice) {
        const price = parseFloat(tournament.signupPrice);
        return !isNaN(price) ? price.toFixed(2) : "";
      }

      return "";
    };

    // Create CSV rows
    const rows = tournaments.map((tournament) => {
      const row = [
        tournament.id || "",
        tournament.date
          ? moment.unix(tournament.date).format("DD-MM-YYYY")
          : "",
        tournament.name || "",
        getStatusName(tournament.status),
        tournament.uid || "",
        getSportName(tournament.sportId),
        tournament.individualPlayers || "",
        tournament.color || "",
        typeof tournament.liveLink === "string" &&
        tournament.liveLink.trim() !== ""
          ? "true"
          : "false",
        tournament.ia === true ? "true" : "false",
        tournament.country || "",
        tournament.age && tournament.age[0] ? tournament.age[0] : "",
        tournament.age && tournament.age[1] ? tournament.age[1] : "",
        getLevelName(tournament.level),
        tournament.gender && tournament.gender[0] === 1 ? "true" : "false",
        tournament.gender && tournament.gender[1] === 1 ? "true" : "false",
        tournament.numDivisions || "",
        tournament.numLocations || "",
        tournament.numMatchDays || "",
        tournament.numPlayers || "",
        tournament.teamsAsRefs === true ? "true" : "false",
        tournament.numReferees || "",
        tournament.numTeams || "",
        tournament.numMatches || "",
        tournament.breaks && typeof tournament.breaks === "object"
          ? Object.keys(tournament.breaks).length
          : "",
        tournament.admins && Array.isArray(tournament.admins)
          ? tournament.admins.length
          : "",
        tournament.lang && Array.isArray(tournament.lang)
          ? tournament.lang.length
          : "",
        tournament.fields && typeof tournament.fields === "object"
          ? Object.keys(tournament.fields).length
          : "",
        tournament.customPoints && typeof tournament.customPoints === "object"
          ? Object.keys(tournament.customPoints).length
          : "",
        tournament.products && typeof tournament.products === "object"
          ? Object.keys(tournament.products).length
          : "",
        tournament.questions && typeof tournament.questions === "object"
          ? Object.keys(tournament.questions).length
          : "",
        tournament.sponsors && typeof tournament.sponsors === "object"
          ? Object.keys(tournament.sponsors).length
          : "",
        tournament.matchDuration || "",
        hasCustomMatchDuration(tournament) ? "true" : "false",
        tournament.timeBetweenMatches || "",
        tournament.description !== undefined ? "true" : "false",
        tournament.copy !== undefined ? "true" : "false",
        tournament.useSets === "1" ? "true" : "false",
        tournament.advancedGroupRanking || "",
        tournament.onlinePayment || "",
        tournament.manualPayment || "",
        tournament.currency || "",
        tournament.online === true ? "true" : "false",
        tournament.requireCheckIn === true ? "true" : "false",
        tournament.pricePerDivision === true ? "true" : "false",
        calculateAverageSignupPrice(tournament),
      ];

      return row
        .map((field) => {
          // Escape quotes and wrap in quotes if contains comma or newline
          const escaped = String(field).replace(/"/g, '""');
          if (
            escaped.includes(",") ||
            escaped.includes("\n") ||
            escaped.includes('"')
          ) {
            return `"${escaped}"`;
          }
          return escaped;
        })
        .join(",");
    });

    // Combine headers and rows
    const csvContent = [headers.join(","), ...rows].join("\n");
    return csvContent;
  };

  downloadCSV = () => {
    const { csvData } = this.state;
    if (!csvData) return;

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `tournament_export_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this.addLog("CSV file downloaded successfully");
  };

  render() {
    const {
      loading,
      totalTournaments,
      exportedCount,
      error,
      progress,
      logs,
      csvData,
    } = this.state;

    if (loading) {
      return (
        <div style={{ padding: "20px" }}>
          <Typography variant="h4" gutterBottom>
            Tournament Export
          </Typography>

          <div style={{ marginBottom: "20px" }}>
            <Typography variant="body1" gutterBottom>
              Processing tournaments... {progress.toFixed(1)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              style={{ marginBottom: "10px" }}
            />
            <Typography variant="body2" color="textSecondary">
              Processed {exportedCount} of {totalTournaments} tournaments
            </Typography>
          </div>

          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Process Logs
              </Typography>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  fontFamily: "monospace",
                  fontSize: "12px",
                }}
              >
                {logs.map((log, index) => (
                  <div key={index} style={{ marginBottom: "2px" }}>
                    {log}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: "20px" }}>
          <Typography color="error">{error}</Typography>
        </div>
      );
    }

    return (
      <div style={{ padding: "20px" }}>
        <Typography variant="h4" gutterBottom>
          Tournament Export
        </Typography>

        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "baseline",
            paddingLeft: 10,
            marginBottom: "20px",
          }}
        >
          <KeyboardDatePicker
            margin="normal"
            name="start"
            label="Start Date"
            format={languages.createTournament.dateFormat}
            cancelLabel={languages.createTournament.cancel}
            autoOk={true}
            value={this.state.start}
            onChange={this.handleStartChange}
            placeholder={languages.createTournament.datePlaceholder}
            invalidLabel={languages.createTournament.invalidDateLabel}
          />
          <KeyboardDatePicker
            margin="normal"
            name="end"
            label="End Date"
            format={languages.createTournament.dateFormat}
            cancelLabel={languages.createTournament.cancel}
            autoOk={true}
            value={this.state.end}
            onChange={this.handleEndChange}
            placeholder={languages.createTournament.datePlaceholder}
            invalidLabel={languages.createTournament.invalidDateLabel}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={this.loadTournamentData}
            style={{ height: "30px" }}
            disabled={this.state.loading}
          >
            Generate report
          </Button>
        </div>

        {totalTournaments > 0 && (
          <Grid container spacing={3} style={{ marginBottom: "20px" }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    Total Tournaments
                  </Typography>
                  <Typography variant="h3" style={{ marginTop: "10px" }}>
                    {totalTournaments}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Tournaments with status 1, 2, or 3
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    Export Status
                  </Typography>
                  <Typography variant="h3" style={{ marginTop: "10px" }}>
                    {exportedCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Tournaments processed and ready for export
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.downloadCSV}
                    disabled={!csvData}
                    style={{ marginTop: "10px" }}
                  >
                    Download CSV
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {totalTournaments === 0 && !loading && (
          <Card style={{ marginBottom: "20px" }}>
            <CardContent>
              <Typography variant="h6" color="primary">
                Ready to Export
              </Typography>
              <Typography variant="body1" style={{ marginTop: "10px" }}>
                Select a date range above and click "Export Tournaments" to
                begin the export process.
              </Typography>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" color="primary">
              Export Details
            </Typography>
            <Typography variant="body2" gutterBottom>
              The CSV export includes the following columns:
            </Typography>
            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
                padding: "10px",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            >
              <div>
                id, date, name, status, uid, sportName, individualPlayers,
                color, livelink, ia, country, age_min, age_max, level,
                gender_min, gender_max, numDivisions, numLocations,
                numMatchDays, numPlayers, TeamsAsRefs, numReferees, numTeams,
                numMatches, breaks_length, admins_length, lang_length,
                fields_length, customPoints_length, products_length,
                questions_length, sponsors_length, matchDuration,
                customMatchDuration, timeBetweenMatches, description, copy,
                useSets, advancedGroupRanking, onlinePayment, manualPayment,
                currency, online, requireCheckIn, averageSignupPrice
              </div>
            </div>
          </CardContent>
        </Card>

        {logs.length > 0 && (
          <Card style={{ marginTop: "20px" }}>
            <CardContent>
              <Typography variant="h6" color="primary">
                Process Logs
              </Typography>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  fontFamily: "monospace",
                  fontSize: "12px",
                }}
              >
                {logs.map((log, index) => (
                  <div key={index} style={{ marginBottom: "2px" }}>
                    {log}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }
}

export default TournamentExportContainer;
